package com.schoolconnect.app.feature.imports

import androidx.compose.ui.graphics.Color

private fun defaultReviewTabs(): List<ImportTabUiModel> = listOf(
    ImportTabUiModel(id = "staff", title = "Staff", count = 0, isSelected = true),
    ImportTabUiModel(id = "subjects", title = "Subjects", count = 0),
    ImportTabUiModel(id = "classes", title = "Classes", count = 0),
    ImportTabUiModel(id = "teaching-assignments", title = "Teaching Assignments", count = 0)
)

private fun defaultReviewPageIndexByTab(): Map<String, Int> = defaultReviewTabs().associate { it.id to 0 }

enum class ImportWorkflowStage {
    ValidationSummary,
    ReviewAndSave
}

data class ImportsUiState(
    val breadcrumbs: List<String> = listOf("Bulk Import", "School Setup", "Import School Details"),
    val pageTitle: String = "Import School Details",
    val validationSubtitle: String = "Upload and validate school information from Excel file before importing to the system.",
    val reviewSubtitle: String = "Review valid records before saving to the system.",
    val backToBulkImportLabel: String = "Back to Bulk Import",
    val backToValidationSummaryLabel: String = "Back to Validation Summary",
    val stage: ImportWorkflowStage = ImportWorkflowStage.ValidationSummary,
    val steps: List<ImportStepUiModel> = listOf(
        ImportStepUiModel(number = 1, title = "Step 1", subtitle = "Upload & Validate", isActive = true),
        ImportStepUiModel(number = 2, title = "Step 2", subtitle = "Review & Save")
    ),
    val existingDetails: List<ImportSummaryRow> = listOf(
        ImportSummaryRow("Workbook", "No file uploaded"),
        ImportSummaryRow("Sheets", "Staff, Subjects, Classes, Teaching Assignments")
    ),
    val newImportDetails: List<ImportSummaryRow> = listOf(
        ImportSummaryRow("File Name", "No file selected"),
        ImportSummaryRow("Selected On", "Not selected"),
        ImportSummaryRow("Selected By", "Not selected"),
        ImportSummaryRow("Total Rows", "0")
    ),
    val validationDetails: List<ImportSummaryRow> = listOf(
        ImportSummaryRow("Total Rows", "0"),
        ImportSummaryRow("Valid Rows", "0", valueColor = Color(0xFF16A34A)),
        ImportSummaryRow("Invalid Rows", "0", valueColor = Color(0xFFDC2626)),
        ImportSummaryRow(
            "Status",
            badgeText = "Awaiting Upload",
            badgeBackground = Color(0xFFFFF4D6),
            badgeColor = Color(0xFFB45309)
        )
    ),
    val validationLastUpdated: String = "Upload an XLSX workbook to begin validation",
    val sheetOptions: List<String> = listOf("All Sheets", "Staff", "Subjects", "Classes", "Teaching Assignments", "Reference Values"),
    val selectedSheet: String = "All Sheets",
    val totalErrorsLabel: String = "Validation Messages",
    val errorHelpText: String = "Upload a workbook to see validation issues or parsed rows.",
    val errorRows: List<ImportErrorRow> = emptyList(),
    val structureValidationErrors: List<String> = emptyList(),
    val errorPageText: String = "Showing 0 to 0 of 0 errors",
    val pageIndex: Int = 0,
    val pageCount: Int = 1,
    val reviewSummary: ImportReviewSummary = ImportReviewSummary(
        fileName = "No file selected",
        uploadedOn = "Not selected",
        readyToSaveRows = 0,
        status = "Awaiting Upload"
    ),
    val reviewTabs: List<ImportTabUiModel> = defaultReviewTabs(),
    val selectedTabId: String = "staff",
    val staffRecords: List<StaffRecordUiModel> = emptyList(),
    val subjectRecords: List<SubjectRecordUiModel> = emptyList(),
    val classRecords: List<ClassRecordUiModel> = emptyList(),
    val assignmentRecords: List<TeachingAssignmentRecordUiModel> = emptyList(),
    val rowsPerPage: Int = com.schoolconnect.app.feature.common.PaginationPolicy.DEFAULT_PAGE_SIZE,
    val reviewPageIndex: Int = 0,
    val reviewPageIndexByTab: Map<String, Int> = defaultReviewPageIndexByTab(),
    val isLoading: Boolean = false,
    val serverValidationCompleted: Boolean = false,
    val serverValidationPassed: Boolean = false,
    val showReviewWarning: Boolean = false,
    val showSaveWarning: Boolean = false,
    val errorMessage: String = ""
)

data class ImportStepUiModel(
    val number: Int,
    val title: String,
    val subtitle: String,
    val isActive: Boolean = false,
    val isCompleted: Boolean = false
)

data class ImportReviewSummary(
    val fileName: String,
    val uploadedOn: String,
    val readyToSaveRows: Int,
    val status: String
)

data class ImportTabUiModel(
    val id: String,
    val title: String,
    val count: Int,
    val isSelected: Boolean = false
)

data class ImportsWorkbookPreview(
    val fileName: String,
    val uploadedOn: String,
    val staffRecords: List<StaffRecordUiModel>,
    val subjectRecords: List<SubjectRecordUiModel>,
    val classRecords: List<ClassRecordUiModel>,
    val assignmentRecords: List<TeachingAssignmentRecordUiModel>,
    val errorRows: List<ImportErrorRow>,
    val validationMessages: List<String>,
    val structureErrors: List<String> = emptyList()
)

data class StaffRecordUiModel(
    val rowNo: Int,
    val staffId: String,
    val staffName: String,
    val gender: String,
    val staffRole: String,
    val authRole: String,
    val mobileNumber: String,
    val emailAddress: String
)

data class SubjectRecordUiModel(
    val rowNo: Int,
    val subjectCode: String,
    val subjectName: String,
    val category: String
)

data class ClassRecordUiModel(
    val rowNo: Int,
    val className: String,
    val section: String,
    val subjectCodes: String
)

data class AssignmentChipUiModel(
    val label: String,
    val background: Color,
    val contentColor: Color
)

data class TeachingAssignmentRecordUiModel(
    val rowNo: Int,
    val staffId: String,
    val staffName: String,
    val classTeacherFor: String,
    val allocations: List<AssignmentChipUiModel>
)

data class ImportSummaryRow(
    val label: String,
    val value: String = "",
    val valueColor: Color = Color(0xFF0A1446),
    val badgeText: String? = null,
    val badgeBackground: Color = Color.Transparent,
    val badgeColor: Color = Color.Unspecified
)

data class ImportErrorRow(
    val sheetName: String,
    val rowNo: String,
    val message: String,
    val id: String = "$sheetName:$rowNo:$message"
)

sealed interface ImportsUiEvent {
    data object BackClicked : ImportsUiEvent
    data object BackToValidationSummaryClicked : ImportsUiEvent
    data object DownloadCurrentDataClicked : ImportsUiEvent
    data object DownloadTemplateClicked : ImportsUiEvent
    data object UploadFileClicked : ImportsUiEvent
    data object ValidateFileClicked : ImportsUiEvent
    data object DownloadErrorDetailsClicked : ImportsUiEvent
    data object FilterClicked : ImportsUiEvent
    data object CancelClicked : ImportsUiEvent
    data object ContinueReviewClicked : ImportsUiEvent
    data object DismissReviewWarning : ImportsUiEvent
    data object ConfirmContinueReviewClicked : ImportsUiEvent
    data object DismissSaveWarning : ImportsUiEvent
    data object ConfirmSaveRecordsClicked : ImportsUiEvent
    data object SaveRecordsClicked : ImportsUiEvent
    data object DismissStructureErrors : ImportsUiEvent
    data class WorkbookPrepared(val fileName: String, val fileBytes: ByteArray) : ImportsUiEvent
    data class WorkbookLoadStarted(val fileName: String) : ImportsUiEvent
    data class WorkbookLoaded(val preview: ImportsWorkbookPreview) : ImportsUiEvent
    data class WorkbookLoadFailed(val message: String) : ImportsUiEvent
    data class TabSelected(val tabId: String) : ImportsUiEvent
    data class RowsPerPageChanged(val rowsPerPage: Int) : ImportsUiEvent
    data class SheetSelected(val sheetName: String) : ImportsUiEvent
    data class PageChanged(val pageIndex: Int) : ImportsUiEvent
}
